import cx from 'classnames'
import take from 'lodash/take'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { uniqBy } from 'lodash'

import { serializeGroupData } from 'lib/group'
import { ROUTE_PATH } from 'lib/paths'
import { selectIsLoggedIn } from 'store/selectors'
import { useGetFollowersCallTimelineQuery } from 'store/api/yay/post'
import { useGetMineQuery } from 'store/api/yay/group'
import { Post } from 'lib/_types'
import { useGetAnonymousCallsQuery } from 'store/api/yay/call'
import { useUsersDefaultSettingsQuery } from 'store/api/yay/user'
import { useMatch } from 'features/i18n/router'

import GroupList from 'components/GroupList'
import CallPostList from 'features/Post/CallPostList'

const SideBarPopupContent = ({
  shouldShowGroupList,
  calls = [],
  groups,
}: {
  shouldShowGroupList: boolean
  groups: {
    data: Array<any>
  }
  calls: Array<any>
}) => {
  if (shouldShowGroupList && groups?.data?.length !== 0) {
    return (
      <GroupList
        groups={groups}
        descriptionMaxLine={6}
        showViewCount={false}
        showMenu={false}
        smallDetailedSettings
      />
    )
  }

  if (calls.length !== 0) {
    return (
      <div className="Sidebar__popup__content__call_list">
        <div className="Sidebar__popup__content__call_list__wrapper">
          <CallPostList calls={calls} />
        </div>
      </div>
    )
  }

  return null
}

const SideBarPopup = ({ visible }: { visible: boolean }) => {
  const { t } = useTranslation()

  const groupMatch = useMatch(ROUTE_PATH.GROUP())
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const myGroupsRes = useGetMineQuery(
    {
      isSidebar: true,
    },
    {
      skip: !isLoggedIn,
    }
  )

  const { data: followerCallList } = useGetFollowersCallTimelineQuery(
    {},
    {
      skip: !isLoggedIn,
    }
  )

  const isGroupDetailPage = groupMatch != null

  const { data: defaultSettings } = useUsersDefaultSettingsQuery(
    {},
    { skip: window.gon.ENV === 'test' }
  )

  const anonymousCallsRes = useGetAnonymousCallsQuery(null, {
    skip:
      !visible ||
      (isLoggedIn && isGroupDetailPage) ||
      !defaultSettings?.anonymous_call_settings?.enabled,
  })

  const popupGroups = (myGroupsRes?.data ?? []).filter(
    ({ id }) => id.toString() !== groupMatch?.params.groupId
  )

  const calls = useMemo(() => {
    return uniqBy<Post>(
      [
        ...(take(followerCallList?.posts ?? [], 3) as Array<Post>),
        ...take(anonymousCallsRes?.data?.posts ?? [], 3),
      ],
      (x) => x?.id
    )
  }, [anonymousCallsRes, followerCallList])

  if (calls?.length == 0) {
    return null
  }

  return (
    <div
      className={cx('Sidebar__popup', {
        'Sidebar__popup--active':
          visible &&
          (isGroupDetailPage || (!isGroupDetailPage && calls.length !== 0)),
      })}
    >
      <div className="Sidebar__popup__header">
        <div className="Sidebar__popup__header__image" />
        <div className="Sidebar__popup__header__title">
          {isGroupDetailPage
            ? t('sidebar.popup.title_group_detail')
            : t('sidebar.popup.title')}
        </div>
      </div>

      <div className="Box Sidebar__popup__wrapper">
        <SideBarPopupContent
          calls={calls}
          shouldShowGroupList={isGroupDetailPage}
          groups={{
            data: serializeGroupData(take(popupGroups, 3)),
          }}
        />
      </div>
    </div>
  )
}

export default SideBarPopup
